import { useEffect, useState } from "react";
import { Api, ApiError, type AgentSummary, type SharedAgentSummary, type TransferBrief } from "../api.js";
import { useI18n } from "../i18n.js";
import { Badge } from "./ui.js";

export function AgentList({ api, onOpen }: { api: Api; onOpen: (id: string) => void }) {
  const { t } = useI18n();
  const [agents, setAgents] = useState<AgentSummary[] | null>(null);
  const [shared, setShared] = useState<SharedAgentSummary[]>([]);
  const [offers, setOffers] = useState<TransferBrief[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [alsoLeads, setAlsoLeads] = useState(false);
  const [confirming, setConfirming] = useState<AgentSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.transfers().then((r) => live && setOffers(r.incoming ?? []), () => undefined);
    api.listAllAgents().then(
      (l) => {
        if (!live) return;
        setAgents(l.agents);
        setShared(l.shared);
      },
      (e) => live && setError(e instanceof ApiError ? e.message : "Could not load your agents."),
    );
    return () => {
      live = false;
    };
  }, [api]);

  const create = async () => {
    setCreating(true);
    try {
      onOpen((await api.createAgent()).id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not create the agent.");
      setCreating(false);
    }
  };

  const unpublish = async (a: AgentSummary) => {
    setBusyId(a.id);
    try {
      await api.unpublish(a.id);
      setAgents((l) => (l ?? []).map((x) => (x.id === a.id ? { ...x, status: "unpublished" } : x)));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not unpublish the agent.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async () => {
    if (!confirming) return;
    setDeleting(true);
    try {
      await api.deleteAgent(confirming.id, alsoLeads);
      setAlsoLeads(false);
      setAgents((l) => (l ?? []).filter((a) => a.id !== confirming.id));
      setConfirming(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not delete the agent.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="page">
      <div className="row between">
        <h1>Your agents</h1>
        <button className="btn primary" onClick={create} disabled={creating}>
          {creating ? "Creating…" : "Create agent"}
        </button>
      </div>
      {error && <div className="banner warn" role="alert">{error}</div>}
      {offers.length > 0 && (
        <div className="banner ok" role="status">
          <p>{t("transfer.pendingBanner", { n: offers.length })}</p>
          {offers.map((o) => (
            <a key={o.id} className="btn primary" href={`#/transfers/${o.id}`}>
              {t("transfer.review")}: {o.agentName || "…"}
            </a>
          ))}
        </div>
      )}
      {confirming && (
        <div className="banner warn" role="alertdialog" aria-label={`Delete ${confirming.name || "Untitled agent"}`}>
          <p>
            <strong>Delete {confirming.name || "Untitled agent"}?</strong> This permanently removes its knowledge and conversations. The contact details it collected stay in Leads, with the agent's name, unless you tick the box below.
            {confirming.status === "published" && " It is published: the chat window on your website stops answering right away."} This cannot be undone. To only take it offline, use Unpublish instead.
          </p>
          <label className="inline">
            <input type="checkbox" checked={alsoLeads} onChange={(e) => setAlsoLeads(e.target.checked)} /> Also delete the leads this agent collected
          </label>
          <div className="row">
            <button className="btn primary" onClick={() => void remove()} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete agent"}
            </button>
            <button className="btn secondary" onClick={() => setConfirming(null)} disabled={deleting}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {agents === null && !error && <p>Loading…</p>}
      {agents?.length === 0 && (
        <div className="card empty">
          <h2>Create your first agent</h2>
          <p>An agent answers your visitors' questions from the knowledge you give it. It takes a few minutes to set up.</p>
        </div>
      )}
      {!!agents?.length && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Last changed</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a.id}>
                <td>
                  <a href={`#/agents/${a.id}`}>{a.name || "Untitled agent"}</a>
                </td>
                <td>
                  <Badge tone={a.status === "published" ? "ok" : a.status === "unpublished" ? "warn" : "muted"}>{a.status === "published" ? "Published" : a.status === "unpublished" ? "Unpublished" : "Draft"}</Badge>
                  {a.hasUnpublishedChanges && <span className="hint"> unpublished changes</span>}
                </td>
                <td>{new Date(a.updatedAt).toLocaleString()}</td>
                <td>
                  <span className="row">
                    <a className="btn secondary" href={`#/agents/${a.id}`}>
                      Edit
                    </a>
                    <a className="btn secondary" href={`#/agents/${a.id}/share`} aria-label={`${t("agents.share")} ${a.name || "Untitled agent"}`}>
                      {t("agents.share")}
                    </a>
                    {a.status === "published" && (
                      <button className="btn secondary" onClick={() => void unpublish(a)} disabled={busyId === a.id} aria-label={`Unpublish ${a.name || "Untitled agent"}`} title="Takes the chat window offline. Nothing is deleted; you can publish again later.">
                        {busyId === a.id ? "Unpublishing…" : "Unpublish"}
                      </button>
                    )}
                    <button className="btn ghost" onClick={() => setConfirming(a)} aria-label={`Delete ${a.name || "Untitled agent"}`}>
                      Delete
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {shared.length > 0 && (
        <>
          <h2>{t("shared.section")}</h2>
          <table className="table">
            <tbody>
              {shared.map((a) => (
                <tr key={a.id}>
                  <td>
                    <a href={`#/agents/${a.id}`}>{a.name || "Untitled agent"}</a>
                  </td>
                  <td>
                    <Badge tone="info">{t("share.badge")} · {t(`role.${a.role}`)}</Badge> <span className="hint">{t("shared.owner", { account: a.ownerAccountName })}</span>
                  </td>
                  <td>
                    <a className="btn secondary" href={`#/agents/${a.id}`}>
                      {t("shared.open")}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
